/**
 * Service for tracking Electron app usage
 */

import fetchService from '../fetchService';
import { API_BASE_URL } from '../../api/config';
import { isElectron } from '../../utils/electron';

interface ElectronAppLaunchRequest {
  version?: string;
  platform?: string;
  user_agent?: string;
}

interface ElectronAppLaunchResponse {
  success: boolean;
  message: string;
  first_launch: boolean;
}

interface ElectronLoginRequest {
  version?: string;
  platform?: string;
}

interface ElectronLoginResponse {
  success: boolean;
  message: string;
  first_login: boolean;
}

class ElectronTrackingService {
  private getAppVersion(): string {
    // Try to get version from Electron API or package.json
    return (window as any).electronAPI?.getVersion?.() || '0.1.0';
  }

  private getPlatform(): string {
    return navigator.platform || 'unknown';
  }

  private getUserAgent(): string {
    return navigator.userAgent;
  }

  /**
   * Track when the Electron app is launched
   */
  async trackAppLaunch(): Promise<ElectronAppLaunchResponse | null> {
    if (!isElectron()) {
      console.log('[ElectronTracking] Not running in Electron, skipping launch tracking');
      return null;
    }

    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      console.log('[ElectronTracking] User not authenticated, skipping launch tracking');
      return null;
    }

    try {
      const requestData: ElectronAppLaunchRequest = {
        version: this.getAppVersion(),
        platform: this.getPlatform(),
        user_agent: this.getUserAgent()
      };

      console.log('[ElectronTracking] Tracking app launch:', requestData);

      const response = await fetchService(`${API_BASE_URL}/user/electron/launch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      console.log('[ElectronTracking] App launch tracked successfully:', response);
      return response;
    } catch (error) {
      console.error('[ElectronTracking] Failed to track app launch:', error);
      return null;
    }
  }

  /**
   * Track when a user successfully logs in via Electron app
   */
  async trackAppLogin(): Promise<ElectronLoginResponse | null> {
    if (!isElectron()) {
      console.log('[ElectronTracking] Not running in Electron, skipping login tracking');
      return null;
    }

    try {
      const requestData: ElectronLoginRequest = {
        version: this.getAppVersion(),
        platform: this.getPlatform()
      };

      console.log('[ElectronTracking] Tracking app login:', requestData);

      const response = await fetchService(`${API_BASE_URL}/user/electron/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      console.log('[ElectronTracking] App login tracked successfully:', response);
      return response;
    } catch (error) {
      console.error('[ElectronTracking] Failed to track app login:', error);
      return null;
    }
  }

  /**
   * Track app launch on app startup (call this in App.tsx or main entry point)
   */
  async trackAppStartup(): Promise<void> {
    if (!isElectron()) {
      return;
    }

    // Small delay to ensure auth is ready
    setTimeout(() => {
      this.trackAppLaunch();
    }, 2000);
  }
}

export const electronTrackingService = new ElectronTrackingService();
export default electronTrackingService;
